import styled from 'styled-components';

import { fadeIn } from '../../styles/shared.style';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 16px 0;
  animation: ${fadeIn} 0.5s forwards;
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

export const GroupHeader = styled.div<{ $opened: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  background-color: ${({ theme }) => theme.colors.surface.high};
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent.primary};
    outline-offset: -2px;
  }
`;

export const GroupHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const GroupTitleRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  @media (min-width: 1200px) {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
`;

export const GroupName = styled.span`
  font-family: 'Elza', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 19.36px;
  letter-spacing: 0.016em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const GroupCount = styled.span`
  font-family: 'Elza', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 14.52px;
  letter-spacing: 0.016em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const CircularArrow = styled.span<{ $opened: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.surface.low};
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  transform: ${({ $opened }) => ($opened ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;
`;

/*
 * Opens/closes by animating `grid-template-rows` (0fr ↔ 1fr) instead of a
 * fixed `max-height`: the real content height has no cap, so a type with many
 * rows (each row now has price, fee, badge and total) is never clipped. When
 * closed, content is `visibility: hidden` — out of the Tab order and hidden
 * from screen readers, info button included.
 */
export const GroupBody = styled.div<{ $opened: boolean }>`
  display: grid;
  grid-template-rows: ${({ $opened }) => ($opened ? '1fr' : '0fr')};
  background-color: ${({ theme }) => theme.colors.surface.low};
  visibility: ${({ $opened }) => ($opened ? 'visible' : 'hidden')};
  transition: grid-template-rows 0.5s ease, visibility 0.5s;
`;

export const GroupBodyInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

export const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 16px 20px;
`;

type ItemProps = { $hasBadge: boolean; $hasTotal: boolean };

/*
 * Ticket type row.
 *
 * Mobile (prototype, badge above the price line):
 *   [name / N ofertas      ]
 *   [badge                 ]
 *   [price + fee (i)       ]
 *   [total R$ X            ]
 * `ItemSummary` becomes `display: contents` so badge and total occupy grid
 * areas. Badge/total rows only exist when there is a badge/total, otherwise
 * `row-gap` would add phantom spacing.
 *
 * Desktop (Figma 16677-57204): name, count and price on the left; badge and
 * total on the right, vertically centered in the row. DOM order (name →
 * price → badge → total) is the reading order in both layouts.
 */
const mobileAreas = ({ $hasBadge, $hasTotal }: ItemProps) => [
  '\'info\'',
  $hasBadge ? '\'badge\'' : '',
  '\'price\'',
  $hasTotal ? '\'total\'' : '',
].filter(Boolean).join(' ');

export const Item = styled.li<ItemProps>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: ${mobileAreas};
  align-items: center;
  row-gap: 4px;
  column-gap: 16px;
  padding: 12px 0;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface.high};
    margin: 0 -20px;
    padding-left: 20px;
    padding-right: 20px;
  }

  @media (min-width: 768px) {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'info summary'
      'price summary';
  }
`;

export const ItemInfo = styled.div`
  grid-area: info;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

/*
 * Ticket selection button: wraps only the name, with no styling of its own (the
 * row looks unchanged), and its `::after` covers the whole row (`Item` is the
 * `position: relative` ancestor) — it takes clicks anywhere on the row and
 * draws the focus ring around it. The info icon sits above it (`z-index: 1`).
 *
 * `&&` (doubled class) because globalStyles' `button` / `button:hover` /
 * `button:disabled` (red background, padding, radius, transition) have higher
 * specificity than a single class.
 */
export const SelectButton = styled.button`
  && {
    display: flex;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: none;
  }

  &&:hover,
  &&:active,
  &&:focus,
  &&:disabled {
    background: none;
    color: inherit;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible::after {
    outline: 2px solid ${({ theme }) => theme.colors.accent.primary};
    outline-offset: 2px;
  }
`;

export const ItemName = styled.span`
  font-family: 'Elza', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 16.94px;
  letter-spacing: 0.016em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ItemCount = styled.span`
  font-family: 'Elza', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 14.52px;
  letter-spacing: 0.016em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const PriceLine = styled.div`
  grid-area: price;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 4px;
  row-gap: 2px;
  min-width: 0;
  margin-top: 4px;

  @media (min-width: 768px) {
    column-gap: 8px;
  }
`;

/* Mobile more compact (15/12) than desktop (18/14), as in the prototype. */
export const ItemPrice = styled.span`
  font-family: 'Elza', sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 22px;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 28px;
  }
`;

export const FeeGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;

  @media (min-width: 768px) {
    gap: 8px;
  }
`;

export const ItemFee = styled.span`
  font-family: 'Elza', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (min-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

export const ItemSummary = styled.div`
  display: contents;

  @media (min-width: 768px) {
    grid-area: summary;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

export const ItemBadge = styled.span`
  grid-area: badge;
  display: inline-flex;
  justify-self: start;

  &:empty {
    display: none;
  }
`;

export const ItemTotal = styled.span`
  grid-area: total;
  white-space: nowrap;
  font-family: 'Elza', sans-serif;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ItemTotalLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const ItemTotalValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 28px;
  }
`;

export const StockBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  height: 28px;
  padding: 0 12px;
  border-radius: 28px;
  background-color: ${({ theme }) => theme.colors.accent.primary};
  color: #ffffff;
  white-space: nowrap;
  font-family: 'Elza', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.016em;
`;
